/**
 * A simple, dependency-free promise concurrency limiter to prevent
 * overwhelming external APIs or the browser's connection limit.
 */
export function pLimit(concurrency: number) {
  const queue: (() => void)[] = [];
  let activeCount = 0;

  const next = () => {
    activeCount--;
    if (queue.length > 0) {
      const task = queue.shift();
      if (task) task();
    }
  };

  return <T>(fn: () => Promise<T>): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      const task = async () => {
        activeCount++;
        try {
          resolve(await fn());
        } catch (error) {
          reject(error);
        } finally {
          next();
        }
      };

      if (activeCount < concurrency) {
        task();
      } else {
        queue.push(task);
      }
    });
  };
}

/**
 * Global limiter for heavy Firestore aggregate queries (MB1 legacy support).
 * Ensures that no matter where the query is triggered (stats store, mimboxes store, etc.),
 * we never exceed the browser's connection limit or hit backend quotas.
 */
export const mb1AggregateLimiter = pLimit(50);
